import React, { useState, useEffect } from 'react';
import { Button, ButtonGroup } from '@mui/material';
import { ThemeData, saveTheme, getTheme } from '../../api/userApi';

const defaultTheme: ThemeData = {
  colors: {
    primary: '#1976d2',
    primaryLight: '#42a5f5',
    primaryDark: '#1565c0',
    secondary: '#dc004e',
    secondaryLight: '#ff5983',
    secondaryDark: '#9a0036',
    backgroundDefault: '#fafafa',
    backgroundPaper: '#ffffff',
    textPrimary: '#212121',
    textSecondary: '#757575',
    error: '#f44336',
    warning: '#ff9800',
    info: '#2196f3',
    success: '#4caf50',
    borderColor: '#e0e0e0',
    authFieldBackground: 'rgba(255, 255, 255, 0.1)',
  },
  typography: {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontSize: '14px',
    h1Size: '2.125rem',
    h2Size: '1.5rem',
    h3Size: '1.25rem',
  },
  spacing: {
    unit: '8px',
    containerPadding: '16px',
    cardMargin: '16px',
  },
  layout: {
    borderRadius: '4px',
    borderRadiusSm: '2px',
    borderRadiusLg: '8px',
  },
  effects: {
    shadowLight: '0 1px 3px rgba(0,0,0,0.12)',
    shadowMedium: '0 4px 6px rgba(0,0,0,0.16)',
    shadowHeavy: '0 10px 20px rgba(0,0,0,0.19)',
    glassmorphism: false,
    darkMode: false,
    highContrast: false,
  }
};

export const ThemeCustomizer: React.FC = () => {
  const [theme, setTheme] = useState<ThemeData>(defaultTheme);
  const [loading, setLoading] = useState(true); // Start with loading=true to prevent initial theme application
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeSection, setActiveSection] = useState('actions');
  const [themeLoaded, setThemeLoaded] = useState(false); // Track if we've loaded the actual theme

  // Load theme on mount
  useEffect(() => {
    loadTheme();
  }, []);

  // Apply theme to CSS variables when theme changes (but only after initial load)
  useEffect(() => {
    if (themeLoaded) {
      console.log('ThemeCustomizer: Applying theme to CSS after load:', theme);
      applyThemeToCSS(theme);
    }
  }, [theme, themeLoaded]);

  const mergeWithDefaults = (loadedTheme: any): ThemeData => {
    // Merge loaded theme with defaults to ensure all new fields exist
    return {
      colors: { ...defaultTheme.colors, ...loadedTheme.colors },
      typography: { ...defaultTheme.typography, ...loadedTheme.typography },
      spacing: { ...defaultTheme.spacing, ...loadedTheme.spacing },
      layout: { ...defaultTheme.layout, ...loadedTheme.layout },
      effects: { ...defaultTheme.effects, ...loadedTheme.effects }
    };
  };

  const loadTheme = async () => {
    setLoading(true);
    try {
      console.log('ThemeCustomizer: Loading theme...');

      // Priority 1: Check JSONP (shared theme)
      if ((window as any).BROODbookThemes && (window as any).BROODbookThemes.current) {
        const loadedTheme = (window as any).BROODbookThemes.current;
        console.log('ThemeCustomizer: Theme loaded from JSONP:', loadedTheme);
        const mergedTheme = mergeWithDefaults(loadedTheme);
        setTheme(mergedTheme);
        setThemeLoaded(true);
      } else {
        // Priority 2: Try API
        try {
          const response = await getTheme();
          if (response.data?.data?.theme) {
            console.log('ThemeCustomizer: Theme loaded from API:', response.data.data.theme);
            const mergedTheme = mergeWithDefaults(response.data.data.theme);
            setTheme(mergedTheme);
            setThemeLoaded(true);
          } else {
            throw new Error('No theme from API');
          }
        } catch (apiError) {
          console.log('ThemeCustomizer: API failed, checking localStorage...');
          // Priority 3: localStorage fallback
          try {
            const storedTheme = localStorage.getItem('broodbook-theme');
            if (storedTheme) {
              const loadedTheme = JSON.parse(storedTheme);
              console.log('ThemeCustomizer: Theme loaded from localStorage:', loadedTheme);
              const mergedTheme = mergeWithDefaults(loadedTheme);
              setTheme(mergedTheme);
              setThemeLoaded(true);
            } else {
              console.log('ThemeCustomizer: No theme found anywhere, using defaults');
              setTheme(defaultTheme);
              setThemeLoaded(true);
            }
          } catch (storageError) {
            console.warn('ThemeCustomizer: localStorage theme parse failed:', storageError);
            setTheme(defaultTheme);
            setThemeLoaded(true);
          }
        }
      }
    } catch (error: any) {
      console.error('ThemeCustomizer: Failed to load theme:', error);
      setTheme(defaultTheme);
      setThemeLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const applyThemeToCSS = (themeData: ThemeData) => {
    const root = document.documentElement;

    // Apply colors with proper CSS variable names
    const colorMap: Record<string, string> = {
      'primary': '--primary-color',
      'primaryLight': '--primary-light',
      'primaryDark': '--primary-dark',
      'secondary': '--secondary-color',
      'secondaryLight': '--secondary-light',
      'secondaryDark': '--secondary-dark',
      'backgroundDefault': '--background-default',
      'backgroundPaper': '--background-paper',
      'textPrimary': '--text-primary',
      'textSecondary': '--text-secondary',
      'error': '--error-color',
      'warning': '--warning-color',
      'info': '--info-color',
      'success': '--success-color',
      'borderColor': '--border-color',
      'authFieldBackground': '--auth-field-background'
    };

    Object.entries(themeData.colors).forEach(([key, value]) => {
      const cssVarName = colorMap[key] || `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVarName, value);
    });

    // Apply typography
    root.style.setProperty('--font-family', themeData.typography.fontFamily);
    root.style.setProperty('--font-size', themeData.typography.fontSize);
    root.style.setProperty('--h1-size', themeData.typography.h1Size);
    root.style.setProperty('--h2-size', themeData.typography.h2Size);
    root.style.setProperty('--h3-size', themeData.typography.h3Size);

    // Apply spacing
    root.style.setProperty('--spacing-unit', themeData.spacing.unit);
    root.style.setProperty('--container-padding', themeData.spacing.containerPadding);
    root.style.setProperty('--card-margin', themeData.spacing.cardMargin);

    // Apply layout
    root.style.setProperty('--border-radius', themeData.layout.borderRadius);
    root.style.setProperty('--border-radius-sm', themeData.layout.borderRadiusSm);
    root.style.setProperty('--border-radius-lg', themeData.layout.borderRadiusLg);

    // Apply effects
    root.style.setProperty('--shadow-light', themeData.effects.shadowLight);
    root.style.setProperty('--shadow-medium', themeData.effects.shadowMedium);
    root.style.setProperty('--shadow-heavy', themeData.effects.shadowHeavy);
  };

  const hexToRgba = (hex: string, alpha: number = 1): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hex;

    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);

    return alpha === 1 ? hex : `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const parseColor = (color: string): { hex: string; alpha: number } => {
    if (color.startsWith('rgba(')) {
      const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([01]?\.?\d*)\)/);
      if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        const alpha = parseFloat(match[4]);
        const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
        return { hex, alpha };
      }
    }
    return { hex: color, alpha: 1 };
  };

  const handleColorChange = (colorKey: keyof ThemeData['colors'], value: string) => {
    setTheme(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorKey]: value
      }
    }));
  };

  const handleOpacityChange = (colorKey: keyof ThemeData['colors'], alpha: number) => {
    const currentColor = theme.colors[colorKey];
    const { hex } = parseColor(currentColor);
    const newColor = hexToRgba(hex, alpha);

    setTheme(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorKey]: newColor
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('Saving theme to JSONP:', theme);
      const response = await saveTheme(theme);
      console.log('Theme save response:', response);

      // Update JSONP window object immediately so all clients see it
      if ((window as any).BROODbookThemes) {
        (window as any).BROODbookThemes.current = theme;
        console.log('Theme updated in window.BROODbookThemes');
      }

      // Also save to localStorage for instant loading on refresh
      localStorage.setItem('broodbook-theme', JSON.stringify(theme));
      console.log('Theme saved to localStorage for instant refresh loading');

      // Force re-apply the theme immediately after saving
      applyThemeToCSS(theme);
      setMessage({ type: 'success', text: 'Theme saved successfully to JSONP!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Failed to save theme:', error);
      console.error('Error details:', error.response?.data);
      setMessage({ type: 'error', text: `Failed to save theme: ${error.response?.data?.message || error.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTheme(defaultTheme);
    // Clear localStorage so refresh will use default theme
    localStorage.removeItem('broodbook-theme');
    console.log('Theme reset to default, localStorage cleared');
    setMessage({ type: 'success', text: 'Theme reset to default!' });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(theme, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'broodbook-theme.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target?.result as string);
        setTheme(imported);
        setMessage({ type: 'success', text: 'Theme imported successfully!' });
        setTimeout(() => setMessage(null), 3000);
      } catch (error) {
        setMessage({ type: 'error', text: 'Invalid theme file format.' });
        setTimeout(() => setMessage(null), 3000);
      }
    };
    reader.readAsText(file);
  };

  if (loading) {
    return (
      // <div className="card">
        <div className="card-content">
          <p>Loading theme customizer...</p>
        </div>
      // </div>
    );
  }

  return (
    // <div className="card mb-3">
      <div className="card-content" style={{ position: 'relative' }}>
        {message && (
          <div
            className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'}`}
            style={{
              position: 'absolute',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              minWidth: '300px',
              maxWidth: '600px',
              margin: 0
            }}
          >
            {message.text}
          </div>
        )}

        {/* Tab Navigation */}
        <ButtonGroup variant="contained" sx={{ mb: 3 }}>
          <Button
            variant={activeSection === 'colors' ? 'contained' : 'outlined'}
            onClick={() => setActiveSection('colors')}
            sx={{
              backgroundColor: activeSection === 'colors' ? 'var(--primary-color)' : 'transparent',
              '&:hover': {
                backgroundColor: activeSection === 'colors' ? 'var(--primary-dark)' : 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            Colors
          </Button>
          <Button
            variant={activeSection === 'actions' ? 'contained' : 'outlined'}
            onClick={() => setActiveSection('actions')}
            sx={{
              backgroundColor: activeSection === 'actions' ? 'var(--primary-color)' : 'transparent',
              '&:hover': {
                backgroundColor: activeSection === 'actions' ? 'var(--primary-dark)' : 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            Actions
          </Button>
        </ButtonGroup>

        {/* Colors Section */}
        {activeSection === 'colors' && (
          <>
            <h4 className="mb-3">Color Scheme</h4>
            <div className="color-grid">
              {Object.entries(theme.colors).map(([key, value]) => {
                const { hex, alpha } = parseColor(value);
                return (
                  <div key={key} className="color-item">
                    <label className="form-label">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </label>
                    <div className="color-input-group" style={{ pointerEvents: 'auto', position: 'relative', zIndex: 1 }}>
                      <input
                        type="color"
                        value={hex}
                        onChange={(e) => {
                          const newValue = alpha === 1 ? e.target.value : hexToRgba(e.target.value, alpha);
                          handleColorChange(key as keyof ThemeData['colors'], newValue);
                        }}
                        className="color-picker"
                        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => handleColorChange(key as keyof ThemeData['colors'], e.target.value)}
                        className="form-input color-text"
                        placeholder="#000000 or rgba(255,255,255,0.5)"
                        style={{ pointerEvents: 'auto', cursor: 'text' }}
                      />
                    </div>
                    <div className="opacity-controls" style={{ pointerEvents: 'auto', position: 'relative', zIndex: 1 }}>
                      <label className="form-label-sm">Opacity: {Math.round(alpha * 100)}%</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={alpha}
                        onChange={(e) => handleOpacityChange(key as keyof ThemeData['colors'], parseFloat(e.target.value))}
                        className="opacity-slider"
                        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Actions Section */}
        {activeSection === 'actions' && (
          <div>
            <h4 className="mb-3">Theme Actions</h4>
            <div className="btn-group">
              <button
                className="btn btn-contained"
                onClick={handleSave}
                disabled={saving}
              >
                <span className="material-icons mr-1">
                  {saving ? 'hourglass_empty' : 'save'}
                </span>
                {saving ? 'Saving...' : 'Save Theme'}
              </button>

              <button
                className="btn btn-outlined"
                onClick={handleReset}
              >
                <span className="material-icons mr-1">refresh</span>
                Reset to Default
              </button>

              <button
                className="btn btn-outlined"
                onClick={handleExport}
              >
                <span className="material-icons mr-1">download</span>
                Export Theme
              </button>

              <label className="btn btn-outlined">
                <span className="material-icons mr-1">upload</span>
                Import Theme
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
        )}
      </div>
    // </div>
  );
};